/**
 * Feature Detection and Lazy Loading System
 * 
 * Dynamically loads feature bundles based on DOM content detection.
 * Provides a smart loading system that:
 * - Detects features present in the page
 * - Loads bundles asynchronously in correct order
 * - Handles dependencies between bundles
 * - Prevents duplicate loading
 * - Provides hooks for custom initialization
 * 
 * @package logic
 */

class FeatureLoader {
    constructor() {
        this.loadedBundles = new Set();
        this.loadingBundles = new Map(); // bundle => Promise
        this.themeUrl = window.logicTheme?.themeUrl || '';
        this.debug = window.logicTheme?.debug || false;
        
        // Check for bundles already loaded by PHP (server-side enqueue)
        this.detectAlreadyLoadedBundles();
        
        // Build bundle definitions from PHP config (single source of truth)
        this.bundles = this.buildBundlesFromConfig();

        this.log('FeatureLoader initialized');
    }

    /**
     * Build bundle definitions from PHP-provided config
     * This ensures PHP and JS use the same detection rules
     * 
     * @returns {Object} Bundle definitions
     */
    buildBundlesFromConfig() {
        const config = window.logicTheme?.featureConfig || {};
        const bundles = {};

        for (const [featureName, featureConfig] of Object.entries(config)) {
            const selectors = featureConfig.selectors || [];
            
            bundles[featureName] = {
                url: `${this.themeUrl}/js/theme-${featureName}.min.js`,
                detect: () => {
                    // Check if any selector matches elements in DOM
                    return selectors.some(selector => {
                        try {
                            return document.querySelector(selector) !== null;
                        } catch (e) {
                            // Invalid selector, skip
                            this.log(`Invalid selector: ${selector}`);
                            return false;
                        }
                    });
                },
                dependencies: featureConfig.dependencies || [],
                priority: featureConfig.priority || 5,
                loadStrategy: featureConfig.loadStrategy || 'immediate'
            };
        }

        this.log('Bundles configured from PHP:', Object.keys(bundles));
        return bundles;
    }

    /**
     * Detect bundles already loaded via PHP (server-side enqueue)
     * Prevents duplicate loading when async-features.php already enqueued bundles
     */
    detectAlreadyLoadedBundles() {
        const scripts = document.querySelectorAll('script[src]');
        const config = window.logicTheme?.featureConfig || {};
        
        // Build bundle map dynamically from config
        const bundleMap = {};
        for (const featureName of Object.keys(config)) {
            bundleMap[`theme-${featureName}`] = featureName;
        }

        scripts.forEach(script => {
            const src = script.src;
            for (const [fileName, bundleName] of Object.entries(bundleMap)) {
                if (src.includes(fileName)) {
                    this.loadedBundles.add(bundleName);
                    this.log(`Bundle already enqueued by PHP: ${bundleName}`);
                }
            }
        });
        
        // Also mark server-side detected features as "known"
        // (they may still need loading if PHP didn't enqueue them)
        const detected = window.logicTheme?.detected || [];
        if (detected.length > 0) {
            this.log('Server-side detected features:', detected);
        }
    }

    /**
     * Check if a bundle's script is already in the DOM
     * @param {string} bundleName 
     * @returns {boolean}
     */
    isScriptAlreadyInDOM(bundleName) {
        const config = this.bundles[bundleName];
        if (!config) return false;
        
        // Extract filename from URL for matching
        const urlParts = config.url.split('/');
        const filename = urlParts[urlParts.length - 1];
        
        const scripts = document.querySelectorAll('script[src]');
        for (const script of scripts) {
            if (script.src.includes(filename)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Log debug messages if debug mode is enabled
     */
    log(...args) {
        if (this.debug) {
            console.log('[FeatureLoader]', ...args);
        }
    }

    /**
     * Detect which features are present on the page
     * @returns {string[]} Array of bundle names that should be loaded
     */
    detectFeatures() {
        const needed = [];
        
        for (const [bundleName, config] of Object.entries(this.bundles)) {
            if (config.detect()) {
                needed.push(bundleName);
                this.log(`Detected feature: ${bundleName}`);
            }
        }

        return needed;
    }

    /**
     * Resolve dependencies for a bundle
     * @param {string} bundleName 
     * @returns {string[]} Array of bundle names including dependencies
     */
    resolveDependencies(bundleName) {
        const resolved = [];
        const seen = new Set();

        const resolve = (name) => {
            if (seen.has(name)) return;
            seen.add(name);

            const config = this.bundles[name];
            if (!config) {
                console.warn(`[FeatureLoader] Unknown bundle: ${name}`);
                return;
            }

            // Resolve dependencies first
            if (config.dependencies && config.dependencies.length > 0) {
                config.dependencies.forEach(dep => resolve(dep));
            }

            resolved.push(name);
        };

        resolve(bundleName);
        return resolved;
    }

    /**
     * Load a single bundle
     * @param {string} bundleName 
     * @returns {Promise}
     */
    async loadBundle(bundleName) {
        // Already loaded (either by PHP or previous JS load)
        if (this.loadedBundles.has(bundleName)) {
            this.log(`Bundle already loaded: ${bundleName}`);
            return Promise.resolve();
        }

        // Double-check: Script might have been added to DOM after our initial check
        if (this.isScriptAlreadyInDOM(bundleName)) {
            this.loadedBundles.add(bundleName);
            this.log(`Bundle found in DOM (late detection): ${bundleName}`);
            return Promise.resolve();
        }

        // Currently loading
        if (this.loadingBundles.has(bundleName)) {
            this.log(`Bundle already loading: ${bundleName}`);
            return this.loadingBundles.get(bundleName);
        }

        const config = this.bundles[bundleName];
        if (!config) {
            return Promise.reject(new Error(`Unknown bundle: ${bundleName}`));
        }

        // Add cache-busting version using build timestamp
        const version = window.logicTheme?.buildTimestamp || Date.now();
        const urlWithVersion = `${config.url}?ver=${version}`;

        this.log(`Loading bundle: ${bundleName} from ${urlWithVersion}`);

        // Create loading promise
        const loadPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = urlWithVersion;
            script.async = true;
            
            script.onload = () => {
                this.log(`Bundle loaded: ${bundleName}`);
                this.loadedBundles.add(bundleName);
                this.loadingBundles.delete(bundleName);
                
                // Dispatch custom event
                document.dispatchEvent(new CustomEvent('bundleLoaded', {
                    detail: { bundle: bundleName }
                }));
                
                resolve();
            };

            script.onerror = () => {
                console.error(`[FeatureLoader] Failed to load bundle: ${bundleName}`);
                this.loadingBundles.delete(bundleName);
                reject(new Error(`Failed to load ${bundleName}`));
            };

            document.head.appendChild(script);
        });

        this.loadingBundles.set(bundleName, loadPromise);
        return loadPromise;
    }

    /**
     * Load bundles with their dependencies in correct order
     * @param {string[]} bundleNames 
     * @returns {Promise}
     */
    async loadBundlesWithDependencies(bundleNames) {
        // Resolve all dependencies
        const allBundles = new Set();
        bundleNames.forEach(name => {
            this.resolveDependencies(name).forEach(dep => allBundles.add(dep));
        });

        // Sort by priority (higher priority first)
        const sortedBundles = Array.from(allBundles).sort((a, b) => {
            const priorityA = this.bundles[a]?.priority || 0;
            const priorityB = this.bundles[b]?.priority || 0;
            return priorityB - priorityA;
        });

        this.log('Loading bundles in order:', sortedBundles);

        // Load bundles sequentially to respect dependencies
        for (const bundleName of sortedBundles) {
            try {
                await this.loadBundle(bundleName);
            } catch (error) {
                console.error(`[FeatureLoader] Error loading ${bundleName}:`, error);
            }
        }
    }

    /**
     * Auto-detect and load all needed features
     * @returns {Promise}
     */
    async autoLoad() {
        const needed = this.detectFeatures();
        
        if (needed.length === 0) {
            this.log('No additional features needed');
            return;
        }

        // Separate bundles by loading strategy
        const immediate = needed.filter(name => this.bundles[name]?.loadStrategy !== 'idle');
        const idle = needed.filter(name => this.bundles[name]?.loadStrategy === 'idle');

        // Load immediate features first
        if (immediate.length > 0) {
            this.log(`Auto-loading immediate features: ${immediate.join(', ')}`);
            await this.loadBundlesWithDependencies(immediate);
        }

        // Load idle features when browser is idle (better PageSpeed)
        if (idle.length > 0) {
            this.log(`Scheduling idle features: ${idle.join(', ')}`);
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => {
                    this.log(`Loading idle features: ${idle.join(', ')}`);
                    this.loadBundlesWithDependencies(idle);
                }, { timeout: 2000 });
            } else {
                // Fallback for browsers without requestIdleCallback
                setTimeout(() => {
                    this.log(`Loading idle features (fallback): ${idle.join(', ')}`);
                    this.loadBundlesWithDependencies(idle);
                }, 1000);
            }
        }

        this.log('Feature loading scheduled');
    }

    /**
     * Manually load specific features
     * @param {string|string[]} features 
     * @returns {Promise}
     */
    async load(features) {
        const bundleNames = Array.isArray(features) ? features : [features];
        await this.loadBundlesWithDependencies(bundleNames);
    }

    /**
     * Check if a bundle is loaded
     * @param {string} bundleName 
     * @returns {boolean}
     */
    isLoaded(bundleName) {
        return this.loadedBundles.has(bundleName);
    }

    /**
     * Check if a bundle is currently loading
     * @param {string} bundleName 
     * @returns {boolean}
     */
    isLoading(bundleName) {
        return this.loadingBundles.has(bundleName);
    }

    /**
     * Wait for a bundle to be loaded
     * @param {string} bundleName 
     * @returns {Promise}
     */
    async waitFor(bundleName) {
        if (this.isLoaded(bundleName)) {
            return Promise.resolve();
        }
        
        if (this.isLoading(bundleName)) {
            return this.loadingBundles.get(bundleName);
        }

        return this.loadBundle(bundleName);
    }

    /**
     * Re-scan the page for features (useful after AJAX content load)
     * @returns {Promise}
     */
    async rescan() {
        this.log('Rescanning page for features...');
        await this.autoLoad();
    }
}

// Initialize and export
const featureLoader = new FeatureLoader();

// Auto-load on DOMContentLoaded if not already loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        featureLoader.autoLoad();
    });
} else {
    // DOM already loaded, run immediately
    featureLoader.autoLoad();
}

// Make available globally
window.featureLoader = featureLoader;

// Listen for dynamic content events (like AJAX page loads)
document.addEventListener('contentLoaded', () => {
    featureLoader.rescan();
});

// Listen for Infinite Scroll or similar
document.addEventListener('post-load', () => {
    featureLoader.rescan();
});

export default featureLoader;
